import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva("inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2", {
    variants: {
        variant: {
            default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
            secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
            white: "bg-white text-white-foreground hover:bg-secondary/80",
            destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
            gray: "border-transparent bg-gray-500 text-white",
            blue: "border-transparent bg-blue-500 text-white",
            purple: "border-transparent bg-purple-500 text-white",
            amber: "border-transparent bg-amber-400 text-black",
            red: "border-transparent bg-red-500 text-white",
            pink: "border-transparent bg-pink-500 text-white",
            green: "border-transparent bg-green-600 text-white",
            teal: "border-transparent bg-teal-600 text-white",
            outline: "text-foreground"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

/** Design system tones (`tone-*-bg` / `tone-*-fg`). The backend picks the tone; a screen never maps a status to one. */
export type BadgeTone = "neutral" | "info" | "success" | "warning" | "danger" | "violet" | "teal" | "pink";

const TONE_CLASSES: Record<BadgeTone, string> = {
    neutral: "bg-gray-100 text-gray-700",
    info: "bg-blue-100 text-blue-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-red-100 text-red-800",
    violet: "bg-violet-100 text-violet-800",
    teal: "bg-teal-100 text-teal-800",
    pink: "bg-pink-100 text-pink-800"
};

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof badgeVariants> & {
        /** Takes precedence over `variant`. */
        tone?: BadgeTone;
    };

function Badge({ className, variant, tone, ...props }: BadgeProps) {
    if (tone) {
        return <span className={cn("inline-flex items-center whitespace-nowrap rounded-sm px-2 py-0.5 text-xs font-medium", TONE_CLASSES[tone], className)} {...props} />;
    }
    return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
